/**
 * CP5Actions — portrait action buttons + metadata.
 * data-screen-label="cp-05 Portrait actions"
 */

import { useTranslation } from 'react-i18next';
import { CPBackdrop, CPAvatar } from './shared';
import { formatShortDate } from '../../../lib/format-date';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import styles from './Couple.module.css';

const R2_BASE_URL = (import.meta.env['VITE_R2_PUBLIC_BASE_URL'] as string | undefined) ?? 'https://cdn.calestea.com';

interface CP5ActionsProps {
  flow: UseCoupleFlowReturn;
  onPrivacy: () => void;
  onShare?: () => void;
}

export function CP5Actions({ flow, onPrivacy, onShare }: CP5ActionsProps): JSX.Element {
  const { t, i18n } = useTranslation(['flows', 'common']);
  const portraitUrl = flow.portraitR2Key ? `${R2_BASE_URL}/${flow.portraitR2Key}` : null;
  const publicUrl = flow.couplePortraitId
    ? `https://calestea.com/couple/${flow.couplePortraitId}`
    : null;

  function handleSave(): void {
    if (!portraitUrl) return;
    const a = document.createElement('a');
    a.href = portraitUrl;
    a.download = `couple-portrait.jpg`;
    a.rel = 'noopener noreferrer';
    a.click();
  }

  function handleShare(): void {
    if (!portraitUrl) return;
    if (navigator.share) {
      void navigator.share({
        url: portraitUrl,
        title: flow.coupleName ?? t('couple.actions.shareTitleFallback'),
      });
    } else {
      void navigator.clipboard.writeText(portraitUrl);
    }
    onShare?.();
  }

  function handleTGCover(): void {
    alert(t('common:states.comingSoon'));
  }

  return (
    <div className={styles.root} data-screen-label="cp-05 Portrait actions">
      <CPBackdrop tone="quiet" />

      <div className={styles.content}>
        {/* Portrait image */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <div className={styles.portraitCard}>
            {portraitUrl ? (
              <img src={portraitUrl} alt={t('couple.reveal.portraitAlt')} className={styles.portraitImg} />
            ) : (
              <div className={styles.portraitSkeleton} />
            )}
          </div>
        </div>

        {/* Overlapping avatar pill */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
          <CPAvatar glyph="🌙" palette="gold" size={36} />
          <div style={{ marginLeft: -10 }}>
            <CPAvatar glyph="✦" palette="rose" size={36} />
          </div>
        </div>

        {/* 2×2 action grid */}
        <div className={styles.actionGrid}>
          <button type="button" className={styles.actionBtn} onClick={handleSave}>
            💾 {t('common:actions.save')}
          </button>
          <button type="button" className={styles.actionBtn} onClick={handleShare}>
            ↗ {t('common:actions.share')}
          </button>
          <button type="button" className={styles.actionBtn} onClick={handleTGCover}>
            👤 {t('couple.actions.tgCover')}
          </button>
          <button type="button" className={styles.actionBtn} onClick={onPrivacy}>
            🔒 {t('couple.actions.privacy')}
          </button>
        </div>

        {/* Metadata table */}
        <div className={styles.metaTable}>
          {flow.coupleName && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>{t('couple.actions.metaName')}</span>
              <span className={styles.metaValue}>{flow.coupleName}</span>
            </div>
          )}
          {flow.archetypeBlend && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>{t('couple.actions.metaArchetypes')}</span>
              <span className={styles.metaValue}>{flow.archetypeBlend}</span>
            </div>
          )}
          {publicUrl && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>{t('couple.actions.metaPublicPage')}</span>
              <span className={styles.metaValue}>{publicUrl}</span>
            </div>
          )}
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>{t('couple.actions.metaBrewed')}</span>
            <span className={styles.metaValue}>
              {formatShortDate(new Date(), i18n.language)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
